import * as React from 'react';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import {
    getItemDetailConditionReport,
    getItemDetailDescription,
    getItemDetailDimensions,
    getItemDetailWeight,
} from '@/redux/modules/itemDetail';
import { getItemModelCatalogId, getItemModelStartTime } from '@/redux/modules/item/summary/itemSummary.selectors';
import { H5 } from '@liveauctioneers/hammer-ui-core/text';
import { safelyRender } from '@/utils/safelyRender';
import { useAppSelector } from '@/redux/hooks';
import BuyersPremium from '@/components/BuyersPremium/BuyersPremium';
import DescriptionSection from './DescriptionSection';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';

const FIVE_YEARS_AGO = (Date.now() - 1.577e11) / 1000; // 5 YEARS!

type Props = {
    className?: string;
    itemId: number;
    /** Add walkthrough DOM node */
    walkthrough?: boolean;
};

function ItemDescription({ className = '', itemId, walkthrough }: Props) {
    const catalogId = useAppSelector((state) => getItemModelCatalogId(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemModelStartTime(state, itemId));
    const conditionReport = useAppSelector((state) => getItemDetailConditionReport(state, itemId));
    const description = useAppSelector((state) => getItemDetailDescription(state, itemId));
    const dimensions = useAppSelector((state) => getItemDetailDimensions(state, itemId));
    const weight = useAppSelector((state) => getItemDetailWeight(state, itemId));

    const showDimensions = dimensions.trim() !== '';
    const showWeight = weight.trim() !== '';

    const old = saleStartTs < FIVE_YEARS_AGO;

    return (
        <ItemDescriptionContainer className={className}>
            <StyledTitle forwardedAs="div">
                <FormattedMessage id="bidding_console.console.currentLiveItem.headers.itemDetails" />
            </StyledTitle>
            <DescriptionSection
                body={safelyRender({
                    fontStyle: 1,
                    html: description,
                    old: old,
                    wrapper: 'div',
                })}
                header="bidding_console.console.currentLiveItem.headers.description"
            />
            {conditionReport && (
                <DescriptionSection
                    body={safelyRender({
                        fontStyle: 1,
                        html: conditionReport,
                        wrapper: 'div',
                    })}
                    header="bidding_console.console.currentLiveItem.headers.condition"
                />
            )}
            {showDimensions && (
                <DescriptionSection
                    body={safelyRender({
                        fontStyle: 1,
                        html: dimensions,
                        wrapper: 'div',
                    })}
                    header="bidding_console.console.currentLiveItem.headers.dimensions"
                />
            )}
            {showWeight && (
                <DescriptionSection
                    body={safelyRender({
                        fontStyle: 1,
                        html: weight,
                        wrapper: 'div',
                    })}
                    header="bidding_console.console.currentLiveItem.headers.weight"
                />
            )}
            <BuyersPremium
                catalogId={catalogId}
                walkthrough={walkthrough}
            />
        </ItemDescriptionContainer>
    );
}

export default React.memo<Props>(ItemDescription, isEqual);

const ItemDescriptionContainer = styled.div`
    overflow: hidden;
    position: relative;
    margin: 32px 0 0;
    padding-top: 32px;
    border-top: 1px solid ${({ theme }) => theme.colors.grey400};

    @media print {
        display: flex;
        flex-direction: column;
        > * {
            max-width: 400px;
            margin: 12px auto;
        }
    }
`;

const StyledTitle = styled(H5)`
    margin: 0 0 16px 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-top: 0;
    }
`;
